import { Link } from 'react-router-dom';
import { useReadingList } from '@/hooks/useReadingList';
import { useCategories } from '@/hooks/useNews';

export interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  read_time: string;
  views: number;
  image: string;
  featured?: boolean;
  slug?: string;
}

function newsUrl(item: NewsItem): string {
  return item.slug ? `/haber/${item.slug}` : `/haber/${item.id}`;
}

interface NewsCardProps {
  item: NewsItem;
  size?: 'sm' | 'md' | 'lg';
  showExcerpt?: boolean;
}

export default function NewsCard({ item, size = 'md', showExcerpt = true }: NewsCardProps) {
  const { isSaved, toggleSave } = useReadingList();
  const { categories } = useCategories();
  const saved = isSaved(item.id);
  const category = categories.find(c => c.id === item.category);

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSave(item.id);
  };

  return (
    <div className="group relative">
      <Link to={newsUrl(item)} className="block cursor-pointer">
        <div className="relative overflow-hidden rounded-xl mb-3">
          <div className={`${size === 'sm' ? 'aspect-[4/3]' : 'aspect-[16/10]'} w-full`}>
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          {/* Category badge */}
          <span
            className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold rounded-full"
            style={{
              backgroundColor: `${category?.color}25`,
              color: category?.color,
              backdropFilter: 'blur(4px)',
            }}
          >
            {category?.name}
          </span>
          {/* Read time badge */}
          <span className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-full bg-black/50 text-white/80 backdrop-blur-sm">
            <i className="ri-time-line text-[10px]" />
            {item.read_time}
          </span>
          {/* Views badge */}
          <span className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-full bg-black/50 text-white/80 backdrop-blur-sm">
            <i className="ri-eye-line text-[10px]" />
            {item.views >= 1000 ? `${(item.views / 1000).toFixed(1)}K` : item.views}
          </span>
        </div>

        <h3
          className={`text-white font-bold leading-snug mb-2 group-hover:text-[#00D9FF] transition-colors line-clamp-2 ${
            size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-xl' : 'text-base'
          }`}
        >
          {item.title}
        </h3>

        {showExcerpt && size !== 'sm' && (
          <p className="text-white/50 text-sm line-clamp-2 mb-3">{item.excerpt}</p>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white/40 text-xs">
            <span>{item.date}</span>
          </div>
          <div className="flex items-center gap-3 text-white/35 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-[10px]" />
              {item.read_time}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]" />
              {item.views >= 1000 ? `${(item.views / 1000).toFixed(1)}K` : item.views}
            </span>
          </div>
        </div>
      </Link>

      <button
        onClick={handleSave}
        title={saved ? 'Listeden Çıkar' : 'Okuma Listesine Ekle'}
        className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer z-10 ${
          saved
            ? 'bg-[#00D9FF] text-[#0A0E27]'
            : 'bg-[#0A0E27]/70 text-white/60 hover:bg-[#0A0E27] hover:text-white backdrop-blur-sm'
        }`}
      >
        <i className={`${saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-sm`} />
      </button>
    </div>
  );
}
